import client from "services/api/client";
import { objectToFormData } from 'object-to-formdata';
import {b64toBlob} from "services/utils/utils";
import {Company} from "mapping/company";
import {Comment} from "mapping/comment";
import {Profile} from "mapping/profile";
import {StaticData} from "mapping/static-data";
import {Video} from "mapping/video";
import {Event} from "mapping/event";
import {keyBy} from "lodash";
import {Directory} from "mapping/directory";
import {Invoicing} from "mapping/invoicing";
import {Notification} from "mapping/notification";
import {Thread} from "mapping/thread";
import {Faq} from "mapping/faq";
import {People} from "mapping/people";
import {RecommendationRequest} from "mapping/recommendation_request";
import {Recommendation} from "mapping/recommendation";

export const ENDPOINTS = {
    FORGOT_PASSWORD : "/auth/recovery",
    RESET_PASSWORD : "/auth/reset",
    LOGIN: "/auth/login",
    RESET_TEMP_PASSWORD: "/auth/password/reset-temp",
    SIGNUP: "/auth/register",
    SIGNUP_CONFIRM: "/payment/confirm",
    SIGNUP_FAIL: "/payment/cancel",
    PROFILE: "/profile",
    COMPANIES: "/companies",
    RESET_CODE: "/security_code",
    ASSOCIATE: "/association",
    ONBOARDING_COMPLETE: "/profile/onboard",
    SKILLS: "/skills",
    INTERESTS: "/interests",
    //HOME
    HOME_DATA: "/home",
    //VIDEO
    BASE_VIDEO: "/videos",
    FAVORITE: "/favorite",
    LIKE: "/like",
    USER_SEARCH: "/users/search",
    COMMENT: "/comments",
    UNLOCK: "/unlock",
    SUGGESTED: "/suggested",
    BASE_EVENTS: "/events",
    BASE_DIRECTORY: "/directories",
    ADD_PAYMENT_METHOD: "/payment/add_method",
    REMOVE_PAYMENT_METHOD: "/payment/remove_method",
    EDIT_INVOICING: "/profile/invoicing",
    SEARCH: "/search",
    USER: "/user",
    CHANGE_PASSWORD: "/auth/password",
    DELETION_REQUEST: "/user_deletion_request",

    //Notifications
    BASE_NOTIFICATION: "/notifications",
    UNREAD_NOTIFICATION: "/unread_count",

    BASE_THREADS: "/threads",
    HIGHLIGHTED: "/highlighted",
    BASE_PROFILE: "/profile",
    BASE_COMPANIES: "/companies",
    BASE_USERS: "/users",
    RECOMMENDATION: "/recommendation",
    RECOMMENDATION_REQUEST: "/recommendation_request",
    MESSAGE: "/message"
}

export const forgotPassword = async (email) => {
    return await client.post(ENDPOINTS.FORGOT_PASSWORD, {email: email});
};
export const resetPassword = async (password, token) => {
    return await client.post(ENDPOINTS.RESET_PASSWORD, {token, password});
};
export const login = async (email, password) => {
    return await client.post(ENDPOINTS.LOGIN, {email, password});
};
export const resetTempPassword = async (new_password) => {
    return await client.post(ENDPOINTS.RESET_TEMP_PASSWORD, {password: new_password});
};
export const changePassword = async (new_password, old_password) => {
    return await client.post(ENDPOINTS.CHANGE_PASSWORD, {password: new_password, old_password});
};
export const signup = async (signup, payment, invoice, type) => {
    const data = {
        first_name: signup.firstName,
        last_name: signup.lastName,
        email: signup.email,
        password: signup.password,
        phone: signup.phone,
        invoicing: Invoicing.toServer(invoice),
        payment_methods: [
            payment
        ],
        plan: type
    }
    return await client.post(ENDPOINTS.SIGNUP, data);
};
export const confirmUserSignup = async (hash, plan) => {
    return await client.post(ENDPOINTS.SIGNUP_CONFIRM, {hash, plan})
};
export const cancelUserSignup = async (hash) => {
    return await client.post(ENDPOINTS.SIGNUP_FAIL, {hash})
};

export const getProfile = async () => {
    const {data} = await client.get(ENDPOINTS.PROFILE);
    return {data: Profile.fromServer(data)};
};
export const saveProfile = async (profile) => {
    const data = Profile.toServer(profile);
    const form = objectToFormData(data, {indices: true});
    const image = profile.image;
    if(image && image.src) {
        const blob = b64toBlob(image.src, image.type);
        form.append('avatar', blob);
    }
    return await client.post(ENDPOINTS.PROFILE, form);
};

export const getPublicProfile = async (slug) => {
    const {data} = await client.get(`${ENDPOINTS.USER}/${slug}`);
    return {
        profile: Profile.fromServer(data.profile, false),
        companies: data.companies.map(Company.fromServer),
        videos: data.videos.map(Video.fromServer),
        book: data.book || null,
        achievements: data.profile.achievements,
        recommendations: data.recommendations.map(Recommendation.fromServer)
    };
}

export const saveInvoicingData = async (invoicing) => {
    return await client.put(ENDPOINTS.EDIT_INVOICING, Invoicing.toServer(invoicing));
};

export const getCompanies = async () => {
    const {data} = await client.get(ENDPOINTS.COMPANIES);
    const companies = data.map(Company.fromServer);
    return {data: companies}
};
export const associateCompany = async (name, secret, role) => {
    return await client.post(`${ENDPOINTS.COMPANIES}${ENDPOINTS.ASSOCIATE}`, {business_name: name, security_code: secret, role: role});
};
export const disassociateCompany = async (id) => {
    return await client.delete(`${ENDPOINTS.COMPANIES}/${id}${ENDPOINTS.ASSOCIATE}`);
};
export const disassociateUser = async (userId, companyId) => {
    return await client.delete(`${ENDPOINTS.COMPANIES}/${companyId}${ENDPOINTS.ASSOCIATE}/${userId}`);
};
export const upsertCompany = async (company) => {
    const data = Company.toServer(company);
    const form = objectToFormData(data, {indices: true});
    const image = company.logo;
    if(image && image.src) {
        const blob = b64toBlob(image.src, image.type);
        form.append('logo', blob);
    }
    const doc = company.document;
    if(doc && doc.src) {
        const blob = b64toBlob(doc.src, doc.type);
        form.append('document', blob);
    }
    if (company.id) {
        return await client.post(`${ENDPOINTS.COMPANIES}/${company.id}`, form);
    }
    return await client.post(ENDPOINTS.COMPANIES, form);
};
export const onboardingComplete = async () => {
    return await client.put(`${ENDPOINTS.ONBOARDING_COMPLETE}`);
};
export const companySecretCodeReset = async (id) => {
    return await client.put(`${ENDPOINTS.COMPANIES}/${id}${ENDPOINTS.RESET_CODE}`);
};
export const companyDeleteApi = async (id) => {
    return await client.delete(`${ENDPOINTS.COMPANIES}/${id}`);
};


/* STATIC DATA */
export const getSkills = async () => {
    const {data} = await client.get(ENDPOINTS.SKILLS);
    return data.map(StaticData.fromServer);
};
export const getInterests = async () => {
    const {data} =  await client.get(ENDPOINTS.INTERESTS);
    return data.map(StaticData.fromServer);
};


/* HOME */
export const getHomeData = async () => {
    const {data} = await client.get(ENDPOINTS.HOME_DATA);
    return {
        video: keyBy(data.videos.map(Video.fromServer), "id"),
        recent_video: keyBy(data.recent_videos.map(Video.fromServer), "id"),
        category_video: {
            category: data.category_videos.category,
            list: keyBy(data.category_videos.list.map(Video.fromServer), "id")
        },
        directory_video: keyBy(data.directories.map(Directory.fromServer), "id"),
        events: keyBy((data.events || []).map(Event.fromServer), "id"),
        cta: keyBy((data.ctas || []).map(Video.fromServer), "id"),
        workouts: keyBy((data.trainings || []).map(Video.fromServer), "id"),
        threads: keyBy((data.threads || []).map(Thread.fromServer), "id"),
        users: keyBy((data.users || []).map(People.fromProfile), "id"),
        companies: keyBy((data.companies || []).map(People.fromCompany), "id")
    };
};

/* VIDEO */
export const favoriteVideoApi = async (video_id, favorite) => {
    return await client.put(`${ENDPOINTS.BASE_VIDEO}/${video_id}${ENDPOINTS.FAVORITE}`, {favorite});
}
export const likeVideoApi = async (video_id, like) => {
    return await client.put(`${ENDPOINTS.BASE_VIDEO}/${video_id}${ENDPOINTS.LIKE}`, {like});
}
export const searchVideoApi = async (filters, page) => {
    const params = {
        page: page,
        term: filters.search,
        categories: filters.categories,
        //order_by: filters.order_by,
        order_by: "created_at|desc"
    }
    if(filters.type) {
        params.video_type = filters.type;
    }
    const {data} = await client.get(`${ENDPOINTS.BASE_VIDEO}`, {
        params: params
    });
    const base_index = page * 100;
    const returnData = {
        favorite: keyBy(data.favorite.map(Video.fromServer), "id"),
        directory: keyBy(data.recent_directories.map(Directory.fromServer), "id"),
        video: keyBy(data.videos.videos.map(Video.fromServer).map((i, index) => ({...i, index: index + base_index})), "id")
    }
    return {
        data: returnData,
        metadata: {
            hasNext: data.videos.hasNextPage
        }
    };
}
export const singleVideoApi = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_VIDEO}/${id}`);
    return Video.fromServer(data);
}
export const singleVideoProgressApi = async (id, progress) => {
    return await client.put(`${ENDPOINTS.BASE_VIDEO}/${id}/progress`, {current: progress});
}
export const searchUsers = async (name) => {
    const {data} = await client.post(ENDPOINTS.USER_SEARCH, {name});
    return data;
}
export const sendComment = async (type, id, comment, attachment) => {
    console.log("COMMENT", comment);
    const form = objectToFormData(comment, {indices: true});
    if(attachment && attachment.src) {
        const blob = b64toBlob(attachment.src, attachment.type);
        form.append('attachment', blob);
    }
    const endpoints = {"video": ENDPOINTS.BASE_VIDEO, "event": ENDPOINTS.BASE_EVENTS, "thread": ENDPOINTS.BASE_THREADS};
    const base = endpoints[type];
    return await client.post(`${base}/${id}${ENDPOINTS.COMMENT}`, form);
}
export const getCommentsApi = async (type, id, page, order_by) => {
    const endpoints = {"video": ENDPOINTS.BASE_VIDEO, "event": ENDPOINTS.BASE_EVENTS, "thread": ENDPOINTS.BASE_THREADS};
    const base = endpoints[type];
    const {data} = await client.get(`${base}/${id}${ENDPOINTS.COMMENT}`, {
        params: {
            page: page,
            order_by: order_by
        }
    });
    const base_index = page * 100;
    return {
        data: keyBy(data.comments.map(Comment.fromServer).map((i, index) => ({...i, index: index + base_index})), "id"),
        metadata: {
            hasNext: data.hasNextPage,
            pages: data.pages,
            totalItems: data.total
        }
    };
}
export const deleteCommentApi = async (type, id, comment_id) => {
    const endpoints = {"video": ENDPOINTS.BASE_VIDEO, "event": ENDPOINTS.BASE_EVENTS, "thread": ENDPOINTS.BASE_THREADS};
    const base = endpoints[type];
    return await client.delete(`${base}${ENDPOINTS.COMMENT}/${comment_id}`);
}
export const editCommentApi = async (type, id, comment_id, comment) => {
    const endpoints = {"video": ENDPOINTS.BASE_VIDEO, "event": ENDPOINTS.BASE_EVENTS, "thread": ENDPOINTS.BASE_THREADS};
    const base = endpoints[type];
    return await client.put(`${base}/${id}${ENDPOINTS.COMMENT}/${comment_id}`, comment);
}
export const voteCommentApi = async (type, id, comment_id, vote) => {
    const endpoints = {"video": ENDPOINTS.BASE_VIDEO, "event": ENDPOINTS.BASE_EVENTS, "thread": ENDPOINTS.BASE_THREADS};
    const base = endpoints[type];
    return await client.put(`${base}/${id}${ENDPOINTS.COMMENT}/${comment_id}/vote`, {voted: vote});
}
export const singleDirectoryApi = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_DIRECTORY}/${id}`);
    return Directory.fromServer(data);
}

export const unlockVideo = async (id, payment_method) => {
    return await client.post(`${ENDPOINTS.BASE_VIDEO}/${id}${ENDPOINTS.UNLOCK}`, {payment_method_id: payment_method.payment_id});
}
export const unlockDirectory = async (id, payment_method) => {
    return await client.post(`${ENDPOINTS.BASE_DIRECTORY}/${id}${ENDPOINTS.UNLOCK}`, {payment_method_id: payment_method.payment_id});
}
export const addPaymentMethodApi = async (payment_method) => {
    return await client.post(`${ENDPOINTS.ADD_PAYMENT_METHOD}`, {...payment_method});
}
export const removePaymentMethodApi = async (id) => {
    return await client.get(`${ENDPOINTS.REMOVE_PAYMENT_METHOD}/${id}`);
}
export const suggestedVideo = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_VIDEO}${ENDPOINTS.SUGGESTED}`, {
        params: {
            excluded_id:id
        }
    });
    return keyBy(data.map(Video.fromServer), "id");
}
export const suggestedDirectory = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_DIRECTORY}${ENDPOINTS.SUGGESTED}`, {
        params: {
            excluded_id:id
        }
    });
    return keyBy(data.map(Directory.fromServer), "id");
}
export const searchAll = async (search) => {
    const {data} = await client.get(ENDPOINTS.SEARCH, {
        params: {
            term: search
        }
    });
    return [...data.directories, ...data.events, ...data.videos, ...data.threads, ...data.companies, ...data.users]
}


/* EVENTS */
export const favoriteEventsApi = async (id, favorite) => {
    return await client.put(`${ENDPOINTS.BASE_EVENTS}/${id}${ENDPOINTS.FAVORITE}`, {favorite});
}

export const searchEventsApi = async (filters, page) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_EVENTS}`, {
        params: {
            page: page,
            term: filters.search,
            regions: filters.regions,
            order_by: filters.order_by
        }
    });
    const base_index = page * 100;
    const returnData = {
        favorite: keyBy(data.favorite.map(Event.fromServer), "id"),
        events: keyBy(data.events.events.map(Event.fromServer).map((i, index) => ({...i, index: index + base_index})), "id"),
        old: keyBy(data.past.map(Event.fromServer), "id")
    }
    return {
        data: returnData,
        metadata: {
            hasNext: data.events.hasNextPage
        }
    };
}
export const singleEventApi = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_EVENTS}/${id}`);
    return Event.fromServer(data);
}
export const likeEventApi = async (id, like) => {
    return await client.put(`${ENDPOINTS.BASE_EVENTS}/${id}${ENDPOINTS.LIKE}`, {like});
}
export const suggestedEvent = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_EVENTS}${ENDPOINTS.SUGGESTED}`, {
        params: {
            excluded_id: id
        }
    });
    return keyBy(data.map(Event.fromServer), "id");
}
export const setProfileSettings = async (key, value) => {
    return await client.put(`${ENDPOINTS.PROFILE}/settings`, {
        settings: [
            {name: key, value: value ? 1 : 0}
        ]
    })
}
export const getUserSubscriptionApi = async () => {
    const {data} = await client.get(`${ENDPOINTS.PROFILE}/subscription`)
    return data;
}

export const deleteAccountApi = async () => {
    return await client.post(ENDPOINTS.DELETION_REQUEST);
};

//Notifications
export const getUnreadNotificationApi = async () => {
    const {data} = await client.get(`${ENDPOINTS.BASE_NOTIFICATION}${ENDPOINTS.UNREAD_NOTIFICATION}`);
    return data.unread_count;
}
export const getNotificationListApi = async (page) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_NOTIFICATION}`, {params: {page: page}});
    return {
        ...data,
        notifications: data.notifications.map(Notification.fromServer)
    };
}
export const deleteNotificationListApi = async () => {
    return await client.delete(`${ENDPOINTS.BASE_NOTIFICATION}`);
}


export const searchThreadsApi = async (filters, page) => {
    const params = {
        is_archived: 0,
        page: page,
        term: filters.search,
        categories: filters.categories,
        order_by: filters.order_by
    }
    if (filters.type !== "all") {
        params.type = filters.type;
    }
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}/search`, {
        params
    });
    const base_index = page * 100;
    const returnData = {
        threads: keyBy(data.threads.map(Thread.fromServer).map((i, index) => ({...i, index: index + base_index})), "id"),
    }
    return {
        data: returnData,
        metadata: {
            hasNext: data.hasNextPage
        }
    };
}
export const searchArchivedThreadsApi = async (filters, page) => {
    const params = {
        is_archived: 1,
        page: page,
        term: filters.search,
        categories: filters.categories,
        order_by: filters.order_by
    }
    if (filters.type !== "all") {
        params.type = filters.type;
    }
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}/search`, {
        params
    });
    const base_index = page * 100;
    const returnData = {
        threads: keyBy(data.threads.map(Thread.fromServer).map((i, index) => ({...i, index: index + base_index})), "id"),
    }
    return {
        data: returnData,
        metadata: {
            hasNext: data.hasNextPage
        }
    };
}
export const favoriteThreadApi = async (id, favorite) => {
    return await client.put(`${ENDPOINTS.BASE_THREADS}/${id}${ENDPOINTS.FAVORITE}`, {favorite});
}
export const getThreadsHighlightApi = async () => {
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}${ENDPOINTS.HIGHLIGHTED}`);
    return {
        data: keyBy(data.map(Thread.fromServer), "id")
    };
}
export const getThreadsFavoritesApi = async () => {
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}${ENDPOINTS.FAVORITE}`);
    return {
        data: keyBy(data.map(Thread.fromServer), "id")
    };
}
export const singleThreadApi = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}/${id}`);
    return Thread.fromServer(data);
}
export const threadUpsertApi = async (thread) => {
    const data = Thread.toServer(thread);
    const form = objectToFormData(data, {indices: true});
    const attachment = thread.rawAttachment;
    if(attachment && attachment.src) {
        const blob = b64toBlob(attachment.src, attachment.type);
        form.append('attachment', blob);
    }
    if(thread.id) {
        await client.post(`${ENDPOINTS.BASE_THREADS}/${thread.id}`, form);
        return null;
    }
    const response = await client.post(`${ENDPOINTS.BASE_THREADS}`, form);
    return Thread.fromServer(response.data);
}
export const likeThreadApi = async (id, like) => {
    return await client.put(`${ENDPOINTS.BASE_THREADS}/${id}${ENDPOINTS.LIKE}`, {like});
}

export const deleteThreadApi = async (id) => {
    return await client.delete(`${ENDPOINTS.BASE_THREADS}/${id}`);
}
export const archiveThreadApi = async (id) => {
    return await client.put(`${ENDPOINTS.BASE_THREADS}/${id}/archive`);
}
export const suggestedThreadsApi = async (id) => {
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}${ENDPOINTS.SUGGESTED}`, {
        params: {
            excluded_id:id
        }
    });
    return keyBy(data.map(Thread.fromServer), "id");
}
export const getThreadsLeaderboardsApi = async () => {
    const {data} = await client.get(`${ENDPOINTS.BASE_THREADS}/charts`);
    return keyBy(data, "name");
}
export const getFaqApi = async () => {
    const {data} = await client.get(`/faqs`);
    return {
        data: keyBy(data.map(Faq.fromServer), "id")
    };
}


/* PEOPLES */

export const getPeopleHighlightApi = async () => {
    const {data: companies} = await client.get(`${ENDPOINTS.BASE_COMPANIES}${ENDPOINTS.HIGHLIGHTED}`);
    const {data: profiles} = await client.get(`${ENDPOINTS.BASE_PROFILE}${ENDPOINTS.HIGHLIGHTED}`);
    return {
        profiles: keyBy(profiles.map(People.fromProfile), "id"),
        companies: keyBy(companies.map(People.fromCompany), "id"),
    };
}
export const getPeopleFavoriteApi = async () => {
    const {data: companies} = await client.get(`${ENDPOINTS.BASE_COMPANIES}${ENDPOINTS.FAVORITE}`);
    const {data: profiles} = await client.get(`${ENDPOINTS.BASE_USERS}${ENDPOINTS.FAVORITE}`);
    return {
        profiles: keyBy(profiles.favourites.map(People.fromProfile), "id"),
        companies: keyBy(companies.favourites.map(People.fromCompany), "id"),
    };
}
export const searchPeopleApi = async (filters, page) => {
    const params = {
        page: page,
        term: filters.search,
        order_by: filters.order_by
    }
    if (filters.sector !== "all") {
        params.sector = filters.sector;
    }
    if (filters.osa_hq !== "all") {
        params.osa_hq = filters.osa_hq;
    }
    if (filters.category !== "all") {
        params.category_id = filters.category;
    }
    if (filters.role !== "all") {
        params.role = filters.role;
    }
    if (filters.company_size !== "all") {
        params.company_size = filters.company_size;
    }
    const {data: companies} = await client.get(`${ENDPOINTS.BASE_COMPANIES}/search`, {params});
    const {data: profiles} = await client.get(`${ENDPOINTS.BASE_USERS}/public_search`, {params});

    const base_index = page * 100;
    const returnData = {
        profiles: keyBy(Object.values(profiles.users).map(People.fromProfile).map((i, index) => ({...i, index: index + base_index})), "id"),
        companies: keyBy(Object.values(companies.companies).map(People.fromCompany).map((i, index) => ({...i, index: index + base_index})), "id"),
    }
    return {
        data: returnData,
        metadata: {
            profilesHasNext: profiles.hasNextPage,
            companiesHasNext: companies.hasNextPage
        }
    };
}
export const getPrivateProfile = async (slug) => {
    const {data} = await client.get(`${ENDPOINTS.USER}/${slug}/private`);
    return {
        profile: Profile.fromServer(data.profile, false),
        companies: data.companies.map(Company.fromServer),
        videos: data.videos.map(Video.fromServer),
        book: data.book || null,
        achievements: data.profile.achievements,
        recommendations: data.recommendations.map(Recommendation.fromServer)
    };
}

export const favoritePeopleApi = async (id, favorite, type) => {
    const endpoints = {
        "user": ENDPOINTS.BASE_USERS,
        "company": ENDPOINTS.BASE_COMPANIES
    }
    if(favorite) {
        return await client.put(`${endpoints[type]}/${id}${ENDPOINTS.FAVORITE}`);
    } else {
        return await client.delete(`${endpoints[type]}/${id}${ENDPOINTS.FAVORITE}`);
    }
}

export const confirmSkill = async (name, user_id, type) => {
    await client.post(`${ENDPOINTS.SKILLS}/confirm?skill_name=${name}&${type}_id=${user_id}`);
}

export const getPrivateProfileRecommendation = async (slug) => {
    const {data} = await client.get(`${ENDPOINTS.PROFILE}/${slug}/private${ENDPOINTS.RECOMMENDATION}`);
    return {
        recommendation_enabled: data.recommendation_enabled,
        request_sent: data.request_sent ? RecommendationRequest.fromServer(data.request_sent) : null,
        request_received: data.request_received ? RecommendationRequest.fromServer(data.request_received) : null,
        recommendation_received: data.recommendation_received ? Recommendation.fromServer(data.recommendation_received) : null,
        recommendation_sent: data.recommendation_sent ? Recommendation.fromServer(data.recommendation_sent) : null
    };
}
export const requestRecommendation = async (id) => {
    await client.post(ENDPOINTS.RECOMMENDATION_REQUEST, { user_id: id });
    return true;
}

export const sendRecommendationApi = async (id, text) => {
    return await client.put(`${ENDPOINTS.RECOMMENDATION_REQUEST}/${id}`, {content: text});
};

export const declineRecommendationRequestApi = async (id) => {
    return await client.put(`${ENDPOINTS.RECOMMENDATION_REQUEST}/${id}/decline`);
};
export const deleteRecommendationRequestApi = async (id) => {
    return await client.delete(`${ENDPOINTS.RECOMMENDATION_REQUEST}/${id}`);
};

export const sendMessageApi = async (id, text) => {
    return await client.post(`${ENDPOINTS.MESSAGE}/${id}`, {content: text});
};
export const requestRecommendationsData = async () => {
    const {data} = await client.get(`${ENDPOINTS.PROFILE}${ENDPOINTS.RECOMMENDATION}`);
    return {
        requests_sent: data.requests_sent.map(RecommendationRequest.fromServer),
        requests_received: data.requests_received.map(RecommendationRequest.fromServer),
        recommendation_received: data.recommendation_received.map(Recommendation.fromServer),
        recommendation_sent: data.recommendation_sent.map(Recommendation.fromServer)
    };
}
export const acceptRecommendationApi = async (id) => {
    return await client.put(`${ENDPOINTS.RECOMMENDATION}/${id}/confirm`);
};
export const deleteRecommendationApi = async (id) => {
    return await client.delete(`${ENDPOINTS.RECOMMENDATION}/${id}`);
};
export const setVisibilityRecommendationApi = async (id, hidden) => {
    return await client.put(`${ENDPOINTS.RECOMMENDATION}/${id}/visibility`, {hidden});
};